import React, { useState, useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

const RETENTION_MONTHS = 25

interface MerchantOption {
  label: string;
  value: string;
}

interface InventorySnapshotsListService extends TableService {
  export: (ids: string[], options?: Record<string, unknown>) => Promise<unknown>;
}

interface InventorySnapshotsListProps {
  service: InventorySnapshotsListService; // New prop
  role?: 'admin' | 'operator';
  merchantOptions?: MerchantOption[];
  inventoryTypeOptions?: MerchantOption[];
  addJob?: (job: unknown) => void;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface InventorySnapshotRow {
  id: string;
  merchant_name?: string;
  date?: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  inventory_type?: string;
  batch?: string;
  expiration_date?: string;
  stock?: number;
  allocated_stock?: number;
  available_quantity?: number;
  custom_attributes?: Record<string, unknown>;
  created_at?: string;
  [key: string]: unknown;
}

const InventorySnapshotsList: React.FC<InventorySnapshotsListProps> = ({
  service, // New prop
  role = 'admin',
  merchantOptions = [],
  inventoryTypeOptions = [],
  addJob,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t, i18n } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  const columns = useMemo<unknown[]>(() => [
    ...(role === 'operator' && !hideMerchantUI ? [{
      nameKey: 'models.inventory_snapshot.merchant_name',
      persist: true,
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '150px',
      wrap: true,
      selector: ({ merchant_name }: InventorySnapshotRow) => merchant_name || '-',
    }] : []),
    {
      nameKey: 'models.inventory_snapshot.date',
      persist: true,
      sortable: true,
      sortFieldName: 'inventory_snapshots.date',
      width: '120px',
      wrap: true,
      selector: ({ date }: InventorySnapshotRow) => date || '-',
    },
    {
      nameKey: 'models.inventory_snapshot.product_sku',
      persist: true,
      sortable: true,
      sortFieldName: 'products.sku',
      width: '150px',
      wrap: true,
      selector: ({ product_sku }: InventorySnapshotRow) => product_sku,
    },
    {
      nameKey: 'models.inventory_snapshot.product_name',
      persist: true,
      sortable: true,
      sortFieldName: 'products.name',
      width: '200px',
      wrap: true,
      cell: ({ product_id, product_name }: InventorySnapshotRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/products/${product_id}`} style={{ wordBreak: 'break-word' }}>
            {product_name}
          </Link>
          <a
            href={`/${role}/products/${product_id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: 'models.inventory_snapshot.inventory_type',
      sortable: true,
      sortFieldName: 'inventory_types.name',
      width: '120px',
      wrap: true,
      selector: ({ inventory_type }: InventorySnapshotRow) => inventory_type,
    },
    {
      nameKey: 'models.inventory_snapshot.batch',
      sortable: true,
      sortFieldName: 'inventory_snapshots.batch',
      width: '120px',
      wrap: true,
      selector: ({ batch }: InventorySnapshotRow) => batch || '-',
    },
    {
      nameKey: 'models.inventory_snapshot.expiration_date',
      sortable: true,
      sortFieldName: 'inventory_snapshots.expiration_date',
      width: '120px',
      wrap: true,
      selector: ({ expiration_date }: InventorySnapshotRow) => expiration_date || '-',
    },
    {
      nameKey: 'models.inventory_snapshot.stock',
      name: (
        <Space size={4}>
          {t('models.inventory_snapshot.stock')}
          <Tooltip title={t('shared.inventory_snapshots.list.column_tooltips.stock')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: ({ stock }: InventorySnapshotRow) => stock || 0,
    },
    {
      nameKey: 'models.inventory_snapshot.allocated_stock',
      name: (
        <Space size={4}>
          {t('models.inventory_snapshot.allocated_stock')}
          <Tooltip title={t('shared.inventory_snapshots.list.column_tooltips.allocated_stock')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: ({ allocated_stock }: InventorySnapshotRow) => allocated_stock || 0,
    },
    {
      nameKey: 'models.inventory_snapshot.available_quantity',
      name: (
        <Space size={4}>
          {t('models.inventory_snapshot.available_quantity')}
          <Tooltip title={t('shared.inventory_snapshots.list.column_tooltips.available_quantity')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '120px',
      selector: ({ available_quantity }: InventorySnapshotRow) => available_quantity || 0,
    },
    {
      nameKey: 'models.inventory.custom_attributes',
      sortable: false,
      width: '200px',
      wrap: true,
      cell: ({ custom_attributes }: InventorySnapshotRow) => (
        <CustomAttributesTags attributes={custom_attributes} />
      )
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'inventory_snapshots.id',
      width: '150px',
      selector: ({ created_at }: InventorySnapshotRow) => created_at,
    }
  ], [role, t, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => [
    ...(role === 'operator' && !hideMerchantUI ? [{
      label: t('models.inventory_snapshot.merchant_id'),
      component: MultipleValue,
      inputName: 'merchants.id',
      defaultPinned: true,
      createable: false,
      defaultOptions: merchantOptions,
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.merchant.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.merchant.content'),
      },
    }] : []),
    {
      label: t('models.inventory_snapshot.date'),
      component: FullDayRangePicker,
      inputName: 'inventory_snapshots.date',
      fixed: true,
      allowClear: false,
      defaultValue: [dayjs().subtract(30, 'days').startOf('day'), dayjs().endOf('day')],
      disabledDate: (current: dayjs.Dayjs | null | undefined) => {
        if (!current) return false
        const cutoff = dayjs().subtract(RETENTION_MONTHS, 'month').startOf('month')
        return current.isBefore(cutoff, 'day') || current.isAfter(dayjs(), 'day')
      },
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.date.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.date.content'),
      },
    },
    {
      label: t('models.inventory_snapshot.product_sku'),
      component: MultipleValue,
      inputName: 'products.sku',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.product_sku.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.product_sku.content'),
      },
    },
    {
      label: t('models.inventory_snapshot.product_name'),
      component: MultipleValue,
      inputName: 'products.name',
      createable: true,
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.product_name.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.product_name.content'),
      },
    },
    {
      label: t('models.inventory_snapshot.inventory_type'),
      component: MultipleValue,
      inputName: 'inventory_types.name',
      createable: false,
      defaultOptions: inventoryTypeOptions,
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.inventory_type.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.inventory_type.content'),
      },
    },
    {
      label: t('models.inventory_snapshot.batch'),
      component: MultipleValue,
      inputName: 'inventory_snapshots.batch',
      createable: true,
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.batch.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.batch.content'),
      },
    },
    {
      label: t('models.inventory_snapshot.expiration_date'),
      component: FullDayRangePicker,
      inputName: 'inventory_snapshots.expiration_date',
      tooltip: {
        title: t('shared.inventory_snapshots.list.filter_tooltips.expiration_date.title'),
        content: t('shared.inventory_snapshots.list.filter_tooltips.expiration_date.content'),
      },
    }
  ], [t, role, merchantOptions, inventoryTypeOptions, hideMerchantUI])

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'common.button.export',
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected: Iterable<string>) => executeBatchOperation({
        task: () => service.export(Array.from(selected), { locale: i18n.language }),
        jobName: 'common.button.export'
      })
    } as TableAction
  ], [executeBatchOperation, service, i18n])

  const defaultDateRange = useMemo(() => [
    dayjs().subtract(30, 'days').startOf('day'),
    dayjs().endOf('day')
  ], [])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields: [
          t('models.inventory_snapshot.product_sku'),
          t('models.inventory_snapshot.product_name'),
          t('models.inventory_snapshot.batch'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role.charAt(0).toUpperCase() + role.slice(1)}InventorySnapshotsTable`}
      defaultQuery={{
        sorting_field: 'inventory_snapshots.id',
        sorting_direction: 'desc',
        'inventory_snapshots.date': defaultDateRange,
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.inventory_snapshots.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/inventory_snapshots/list#features`}
    />
  )
}

export default InventorySnapshotsList
